import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SearchableDropdownField } from 'ui-components/components/dropdown';

import { SUPPLIER_CRITERIA_ARGUMENTS } from './static-criteria-arguments';
import { supplierFilterMessages } from './supplier-messages';
import style from './supplier.module.less';
import { OptionFormTypes } from '../../../../services/filtering/filter-form-render-service/option-form-types';

import type { CriteriaInformations, ExistingFiltersHelpers, FilterBy } from './types';

type SupplierFilterComponentProps = {
  criteriaInformations: CriteriaInformations;
  existingFiltersHelpers: ExistingFiltersHelpers;
};

const SupplierFilterComponent = ({ criteriaInformations, existingFiltersHelpers }: SupplierFilterComponentProps) => {
  const { onApplyFilter, onRemoveFilter, existingFilters } = existingFiltersHelpers;
  const { selectedFilterBy, criterionArgumentOptions } = criteriaInformations;
  const intl = useIntl();

  const suppliers = useMemo(
    () =>
      (criterionArgumentOptions ?? []).map((supplier) => ({
        label: supplier.displayName,
        value: supplier.value,
      })),
    [criterionArgumentOptions],
  );

  const selectedSupplierId =
    existingFilters.length > 0
      ? ((Array.isArray(existingFilters[0].criterionArgumentValue)
          ? existingFilters[0].criterionArgumentValue[0]?.toString()
          : existingFilters[0].criterionArgumentValue?.toString()) ?? '')
      : '';

  const handleSupplierChange = (value: string | null) => {
    const existingFilter = existingFilters.length > 0 ? existingFilters[0] : undefined;

    if (!value) {
      if (existingFilter) {
        onRemoveFilter(existingFilter);
      }
      return;
    }

    const criteriaArgument = { value };
    const containsCriterionOperator = { value: 'contains' };
    onApplyFilter(selectedFilterBy, containsCriterionOperator, criteriaArgument, undefined, existingFilter);
  };

  return (
    <div
      className={style['supplier-filter-container']}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      role='presentation'
    >
      <SearchableDropdownField
        className={''}
        id='supplier-filter'
        isClearable
        label={<FormattedMessage {...supplierFilterMessages.selectSupplier} />}
        onItemSelected={handleSupplierChange}
        options={suppliers}
        placeholder={intl.formatMessage(supplierFilterMessages.searchSuppliers)}
        selectedItemValue={selectedSupplierId}
      />
    </div>
  );
};

export const supplierFilterBy: FilterBy = {
  id: 'supplier',
  type: OptionFormTypes.TYPE_IS_OPTIONS,
  filterValue: 'Supplier',
  displayName: 'Supplier',
  shouldHideActionButtons: true,
  expectsArgumentAsArray: false,
  allowsMultipleSelection: false,
  render: (criteriaInformations: CriteriaInformations, existingFiltersHelpers?: ExistingFiltersHelpers) => {
    if (!existingFiltersHelpers) {
      return null;
    }
    return (
      <SupplierFilterComponent
        key='supplier-filter-stable'
        criteriaInformations={criteriaInformations}
        existingFiltersHelpers={existingFiltersHelpers}
      />
    );
  },
};

export const supplierCriteriaArguments = SUPPLIER_CRITERIA_ARGUMENTS;
